const es = {
  header: {
    badge: "Guía Oficial de BotMix",
    title: "Gestión Visual de Oportunidades y CRM — Tablero Kanban",
    summary:
      "El Tablero Kanban transforma tus conversaciones de chat en un pipeline visual de ventas y atención. Organiza los contactos por Etapas del Embudo (ej: 'Nuevo Lead', 'Propuesta Enviada', 'Negociación', 'Ganado', 'Perdido') con arquitectura multimarca, soporte para múltiples Embudos, valor monetario de tratos y filtros comerciales avanzados.",
  },
  sections: [
    {
      id: "jerarquia_marcas_embudos",
      title: "1. Jerarquía Multimarca, Embudos y Etapas",
      description:
        "Organización estructural del ciclo comercial: Marca ➔ Embudo ➔ Etapas.",
      items: [
        {
          id: "selector_marcas",
          title: "Selector y Segmentación Multimarca",
          definition:
            "Permite aislar y clasificar las oportunidades según las diferentes marcas, empresas o líneas de negocio de la organización. Al seleccionar una marca, el tablero adapta de inmediato los Embudos y los clientes vinculados a dicha identidad comercial.",
        },
        {
          id: "selector_embudos",
          title: "Selector de Embudos de Venta (Pipelines)",
          definition:
            "Permite alternar entre diferentes procesos de negocio dentro de la marca seleccionada (ej: 'Ventas Inmobiliarias', 'Postventa', 'Renovaciones SaaS').",
        },
        {
          id: "carriles_etapas",
          title: "Etapas del Embudo (Columnas)",
          definition:
            "Cada Etapa (columna) representa una fase del proceso comercial con su color identificativo, recuento de tickets y suma monetaria total.",
        },
      ],
    },
    {
      id: "interaccion_tarjetas",
      title: "2. Tarjetas de Clientes y Drag & Drop",
      description:
        "Operatividad y movimiento dinámico de tratos en el tablero.",
      items: [
        {
          id: "arrastre_tarjetas",
          title: "Arrastrar y Soltar (Drag & Drop)",
          definition:
            "Mueve un cliente de una Etapa a otra con el ratón. Al soltarlo, el sistema actualiza su Etapa CRM en tiempo real y puede disparar webhooks o flujos automáticos.",
        },
        {
          id: "datos_tarjeta",
          title: "Datos en la Tarjeta de Trato",
          definition:
            "Muestra nombre del cliente, número de WhatsApp, foto de perfil, agente asignado, etiquetas segmentadas y valor estimado del negocio.",
        },
        {
          id: "apertura_directa",
          title: "Acceso Directo a la Conversación",
          definition:
            "Haz clic sobre cualquier tarjeta para abrir inmediatamente el chat lateral y responder al cliente sin perder de vista el Embudo.",
        },
      ],
    },
    {
      id: "metricas_filtros",
      title: "3. Filtros y Métricas Financieras",
      description:
        "Visibilidad ejecutiva sobre el volumen y valor de la cartera de clientes.",
      items: [
        {
          id: "metricas_totales",
          title: "Contador de Tratos y Valor Monetario Acumulado",
          definition:
            "Cada cabecera de Etapa muestra la cantidad exacta de clientes en esa Etapa y la suma total de dinero potencial o cerrado.",
        },
        {
          id: "filtros_agente",
          title: "Filtrado Secuencial: Marca, Embudo, Conexión y Agente",
          definition:
            "Permite a supervisores y agentes filtrar la vista en cascada: comenzando por Marca, luego Embudo, Etapa, Etiqueta, Conexión (canal de WhatsApp/Instagram) y Agente asignado o rango de fechas.",
        },
      ],
    },
  ],
};

const en = {
  header: {
    badge: "Official BotMix Guide",
    title: "Visual Deal & CRM Management — Kanban Board",
    summary:
      "The Kanban Board transforms your chat conversations into a visual sales and support pipeline. Organizes contacts by Funnel Stages (e.g. 'New Lead', 'Proposal Sent', 'Negotiation', 'Won', 'Lost') with multi-brand architecture, multi-funnel support, deal monetary values, and advanced commercial filters.",
  },
  sections: [
    {
      id: "jerarquia_marcas_embudos",
      title: "1. Multi-Brand Hierarchy, Funnels & Stages",
      description:
        "Structural layout of the commercial cycle: Brand ➔ Funnel ➔ Stages.",
      items: [
        {
          id: "selector_marcas",
          title: "Multi-Brand Selector & Segmentation",
          definition:
            "Isolates and organizes sales opportunities across distinct brands, subsidiaries, or business units. Selecting a brand instantly adjusts the available sales funnels and contacts affiliated with that commercial identity.",
        },
        {
          id: "selector_embudos",
          title: "Sales Funnel Selector (Pipelines)",
          definition:
            "Enables toggling between distinct business workflows under the selected brand (e.g. 'Real Estate Sales', 'After-Sales', 'SaaS Renewals').",
        },
        {
          id: "carriles_etapas",
          title: "Funnel Stages (Columns)",
          definition:
            "Each Stage (column) signifies a specific phase in the sales journey with distinctive color accenting, ticket count, and total cumulative currency value.",
        },
      ],
    },
    {
      id: "interaccion_tarjetas",
      title: "2. Customer Cards & Drag & Drop",
      description:
        "Operational workflows and dynamic deal movements across the board.",
      items: [
        {
          id: "arrastre_tarjetas",
          title: "Drag & Drop Mechanics",
          definition:
            "Move a deal from one Stage to another using your mouse. On release, the system updates its CRM Stage in real-time and can trigger webhooks or automated flows.",
        },
        {
          id: "datos_tarjeta",
          title: "Deal Card Information",
          definition:
            "Displays customer name, WhatsApp phone number, profile photo, assigned agent, segmented tags, and estimated deal value.",
        },
        {
          id: "apertura_directa",
          title: "Instant Chat Access",
          definition:
            "Click on any deal card to instantly launch the drawer conversation and interact with the client without navigating away from the board.",
        },
      ],
    },
    {
      id: "metricas_filtros",
      title: "3. Filters & Financial Metrics",
      description:
        "Executive oversight over portfolio volume and monetary pipeline value.",
      items: [
        {
          id: "metricas_totales",
          title: "Deal Counter and Total Accumulated Value",
          definition:
            "Each Stage header presents the precise volume of deals currently in that Stage alongside the accumulated sum of potential or realized revenue.",
        },
        {
          id: "filtros_agente",
          title: "Cascading Filters: Brand, Funnel, Connection & Agent",
          definition:
            "Allows supervisors and sales reps to filter in sequence: starting with Brand, then Funnel, Stage, Tag, Connection (WhatsApp/Instagram channel), and assigned Agent or date range.",
        },
      ],
    },
  ],
};

const pt = {
  header: {
    badge: "Guia Oficial BotMix",
    title: "Gestão Visual de Negócios e CRM — Quadro Kanban",
    summary:
      "O Quadro Kanban transforma suas conversas de chat em um pipeline visual de vendas e suporte. Organiza os contatos por Etapas do Funil (ex: 'Novo Lead', 'Proposta Enviada', 'Negociação', 'Ganho', 'Perdido') com arquitetura multimarca, suporte a múltiplos funis, valor monetário das negociações e filtros comerciais avançados.",
  },
  sections: [
    {
      id: "jerarquia_marcas_embudos",
      title: "1. Hierarquia Multimarca, Funis e Etapas",
      description:
        "Organização estrutural do ciclo comercial: Marca ➔ Funil ➔ Etapas.",
      items: [
        {
          id: "selector_marcas",
          title: "Seletor e Segmentação Multimarca",
          definition:
            "Permite isolar e organizar as oportunidades de acordo com as diferentes marcas, empresas ou linhas de negócio da organização. Ao selecionar uma marca, o quadro ajusta automaticamente os funis e os clientes vinculados a essa identidade comercial.",
        },
        {
          id: "selector_embudos",
          title: "Seletor de Funis de Vendas (Pipelines)",
          definition:
            "Permite alternar entre diferentes processos de negócios dentro da marca selecionada (ex: 'Vendas Imobiliárias', 'Pós-venda', 'Renovações SaaS').",
        },
        {
          id: "carriles_etapas",
          title: "Etapas do Funil (Colunas)",
          definition:
            "Cada Etapa (coluna) representa uma fase da jornada de vendas com cor de destaque, contagem de tickets e valor financeiro total acumulado.",
        },
      ],
    },
    {
      id: "interaccion_tarjetas",
      title: "2. Cartões de Clientes e Drag & Drop",
      description:
        "Operação dinâmica e movimentação de negócios pelo quadro.",
      items: [
        {
          id: "arrastre_tarjetas",
          title: "Arrastar e Soltar (Drag & Drop)",
          definition:
            "Mova um negócio de uma Etapa para outra com o mouse. Ao soltar, o sistema atualiza a Etapa no CRM em tempo real e pode disparar webhooks ou fluxos automáticos.",
        },
        {
          id: "datos_tarjeta",
          title: "Dados do Cartão de Negócio",
          definition:
            "Exibe nome do cliente, número do WhatsApp, foto de perfil, atendente responsável, tags segmentadas e valor estimado da oportunidade.",
        },
        {
          id: "apertura_directa",
          title: "Acesso Direto à Conversa",
          definition:
            "Clique sobre qualquer cartão para abrir imediatamente o chat lateral e conversar com o cliente sem sair do funil.",
        },
      ],
    },
    {
      id: "metricas_filtros",
      title: "3. Filtros e Métricas Financeiras",
      description:
        "Visibilidade executiva sobre volume e valor da carteira de clientes.",
      items: [
        {
          id: "metricas_totales",
          title: "Contador de Negócios e Valor Monetário Acumulado",
          definition:
            "O cabeçalho de cada Etapa mostra a quantidade exata de clientes nessa Etapa e a somatória de receita potencial ou concretizada.",
        },
        {
          id: "filtros_agente",
          title: "Filtros em Cascata: Marca, Funil, Conexão e Atendente",
          definition:
            "Permite que supervisores e vendedores filtrem de forma sequencial: iniciando pela Marca, seguida por Funil, Etapa, Tag, Conexão (WhatsApp/Instagram) e Atendente responsável ou período de datas.",
        },
      ],
    },
  ],
};

const fr = {
  header: {
    badge: "Guide Officiel BotMix",
    title: "Gestion Visuelle des Ventes et CRM — Tableau Kanban",
    summary:
      "Le Tableau Kanban métamorphose vos échanges de chat en un pipeline visuel de vente et de support. Il classe vos contacts par Étapes de l'Entonnoir (ex: 'Nouveau Lead', 'Devis Envoyé', 'Négociation', 'Gagné', 'Perdu') avec architecture multi-marques, prise en charge de multiples tunnels, valorisation financière des dossiers et filtres commerciaux avancés.",
  },
  sections: [
    {
      id: "jerarquia_marcas_embudos",
      title: "1. Hiérarchie Multi-Marques, Entonnoirs et Étapes",
      description:
        "Organisation structurelle du cycle commercial : Marque ➔ Tunnel ➔ Étapes.",
      items: [
        {
          id: "selector_marcas",
          title: "Sélecteur et Segmentation Multi-Marques",
          definition:
            "Permet d'isoler et de structurer les opportunités selon les différentes marques, filiales ou gammes de produits. La sélection d'une marque ajuste immédiatement les tunnels et les prospects rattachés à cette identité commerciale.",
        },
        {
          id: "selector_embudos",
          title: "Sélecteur de Tunnels de Vente (Pipelines)",
          definition:
            "Permet d'alterner entre différents processus métier au sein de la marque choisie (ex: 'Transactions Immobilières', 'Service Après-Vente', 'Renouvellements SaaS').",
        },
        {
          id: "carriles_etapas",
          title: "Étapes de l'Entonnoir (Colonnes)",
          definition:
            "Chaque Étape (colonne) illustre un palier commercial précis avec sa couleur distinctive, le décompte des tickets et le cumul monétaire global.",
        },
      ],
    },
    {
      id: "interaccion_tarjetas",
      title: "2. Cartes Clients et Glisser-Déposer",
      description:
        "Pilotage dynamique et déplacement des dossiers sur le tableau.",
      items: [
        {
          id: "arrastre_tarjetas",
          title: "Glisser-Déposer (Drag & Drop)",
          definition:
            "Déplacez un dossier d'une Étape à l'autre avec la souris. Au relâchement, le CRM met à jour l'Étape en temps réel et peut enclencher des webhooks ou des flux automatisés.",
        },
        {
          id: "datos_tarjeta",
          title: "Données de la Carte d'Affaire",
          definition:
            "Affiche le nom du client, le numéro WhatsApp, la photo de profil, l'agent affecté, les étiquettes et la valorisation estimée du deal.",
        },
        {
          id: "apertura_directa",
          title: "Accès Instantané au Chat",
          definition:
            "Cliquez sur n'importe quelle carte pour ouvrir sur-le-champ la discussion latérale et échanger avec le client sans quitter le pipeline.",
        },
      ],
    },
    {
      id: "metricas_filtros",
      title: "3. Filtres et Indicateurs Financiers",
      description:
        "Visibilité managériale sur le volume et le chiffre d'affaires potentiel du portefeuille.",
      items: [
        {
          id: "metricas_totales",
          title: "Compteur d'Affaires et Valeur Monétaire Cumulée",
          definition:
            "L'en-tête de chaque Étape précise le nombre exact de clients à ce stade et la somme financière potentielle ou conclue.",
        },
        {
          id: "filtros_agente",
          title: "Filtres en Cascade : Marque, Tunnel, Canal et Conseiller",
          definition:
            "Permet aux responsables et agents de filtrer séquentiellement : en commençant par la Marque, puis le Tunnel, l'Étape, l'Étiquette, le Canal de connexion (WhatsApp/Instagram) et le Conseiller assigné ou la période.",
        },
      ],
    },
  ],
};

const ar = {
  header: {
    badge: "الدليل الرسمي لـ BotMix",
    title: "الإدارة المرئية للفرص والصفقات — لوحة كانبان",
    summary:
      "تحول لوحة كانبان محادثات الدردشة إلى مسار مبيعات ودعم مرئي متكامل. تنظم جهات الاتصال في أعمدة حسب المراحل (مثل: 'عميل محتمل جديد'، 'تم إرسال العرض'، 'تفاوض'، 'صفقة ناجحة'، 'صفقة خاسرة') مع معمارية متعددة العلامات التجارية، ودعم مسارات متعددة، وقيم مالية للصفقات، وفلاتر تجارية متطورة.",
  },
  sections: [
    {
      id: "jerarquia_marcas_embudos",
      title: "1. هيكلية العلامات التجارية وأقماع المبيعات والمراحل",
      description:
        "التنظيم الهيكلي لدورة المبيعات: العلامة التجارية ➔ المسار ➔ المراحل.",
      items: [
        {
          id: "selector_marcas",
          title: "محدد وتقسيم العلامات التجارية المتعددة",
          definition:
            "يتيح عزل وتنظيم الفرص التجارية وفقاً لمختلف العلامات التجارية أو الشركات أو خطوط الأعمال التابعة للمؤسسة. عند اختيار علامة تجارية، يقوم اللوح بتكييف مسارات البيع وجهات الاتصال المرتبطة بتلك الهوية التجارية فوراً.",
        },
        {
          id: "selector_embudos",
          title: "محدد مسارات المبيعات (Pipelines)",
          definition:
            "يتيح التبديل بسلاسة بين مسارات العمل المختلفة داخل العلامة التجارية المحددة (مثل: 'المبيعات العقارية'، 'خدمة ما بعد البيع'، 'تجديدات الاشتراكات').",
        },
        {
          id: "carriles_etapas",
          title: "مراحل قمع المبيعات (الأعمدة)",
          definition:
            "تمثل كل مرحلة (عمود) في قمع المبيعات طوراً محدداً بلون مميز، وعدد التذاكر، وإجمالي القيمة المالية التراكمية.",
        },
      ],
    },
    {
      id: "interaccion_tarjetas",
      title: "2. بطاقات العملاء والسحب والإفلات",
      description:
        "التشغيل المرن والحركة التفاعلية للصفقات داخل اللوحة.",
      items: [
        {
          id: "arrastre_tarjetas",
          title: "السحب والإفلات (Drag & Drop)",
          definition:
            "انقل بطاقة العميل من مرحلة لأخرى بالفأرة. بمجرد الإفلات، يحدّث النظام مرحلة العميل في CRM في الوقت الفعلي مع إمكانية تشغيل الويب هوك أو المسارات التلقائية.",
        },
        {
          id: "datos_tarjeta",
          title: "بيانات بطاقة الصفقة",
          definition:
            "تعرض اسم العميل، ورقم الواتساب، والصورة الشخصية، والموظف المسؤول، والوسوم، والقيمة المالية التقديرية للصفقة.",
        },
        {
          id: "apertura_directa",
          title: "الوصول المباشر للمحادثة",
          definition:
            "انقر فوق أي بطاقة لفتح المحادثة الجانبية فوراً والتواصل مع العميل دون مغادرة لوحة المسار.",
        },
      ],
    },
    {
      id: "metricas_filtros",
      title: "3. الفلاتر والمؤشرات المالية",
      description:
        "رؤية إشرافية شاملة على حجم الصفقات وقيمتها المالية الإجمالية.",
      items: [
        {
          id: "metricas_totales",
          title: "عداد الصفقات والقيمة المالية المتراكمة",
          definition:
            "يعرض رأس كل مرحلة العدد الدقيق للعملاء في تلك المرحلة وإجمالي الإيرادات المتوقعة أو المحققة.",
        },
        {
          id: "filtros_agente",
          title: "الفلاتر المتسلسلة: العلامة التجارية، المسار، القناة والموظف",
          definition:
            "تسمح للمشرفين والموظفين بتصفية البيانات بالتسلسل: بدءاً من العلامة التجارية، ثم مسار البيع، والمرحلة، والوسم، وقناة الاتصال (واتساب/إنستغرام)، والموظف المسؤول أو النطاق الزمني.",
        },
      ],
    },
  ],
};

export const KANBAN_HELP_DATA = {
  es,
  en,
  pt,
  fr,
  ar,
};
